/**
 * DeskbayStore — Plugin Capacitor per Apple StoreKit 2.
 *
 * Gestisce i 4 prodotti Apple IAP di Deskbay:
 *   - io.deskbay.app.monthly    → Auto-Renewable Subscription
 *   - io.deskbay.app.yearly     → Auto-Renewable Subscription
 *   - io.deskbay.app.setup.assisted → Non-Consumable (one-time)
 *   - io.deskbay.app.test.1eur      → Consumable (solo admin, test)
 *
 * iOS ONLY. Su Web/Android tutti i pagamenti restano su Stripe.
 * Controllare sempre `platform.isIOS` prima di chiamare qualsiasi metodo.
 *
 * Pattern identico a plugins/secureAuth.ts già presente nel progetto.
 */

import { registerPlugin } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeskbayStoreProduct {
    id: string;
    displayName: string;
    /** Prezzo localizzato da App Store (es. "€19,99") — usare sempre questo in UI */
    displayPrice: string;
    price: string;
    /** 'autoRenewable' | 'nonConsumable' | 'consumable' | 'other' */
    productType: 'autoRenewable' | 'nonConsumable' | 'consumable' | 'other';
    /** 'monthly' | 'yearly' per subscription; 'oneTime' per acquisti singoli */
    period: 'monthly' | 'yearly' | 'oneTime';
}

export interface DeskbayStorePurchaseResult {
    success: boolean;
    /** true = utente ha annullato (non mostrare errore) */
    cancelled?: boolean;
    /** true = in attesa di approvazione genitore (Ask to Buy) */
    pending?: boolean;
    transactionId?: string;
    originalTransactionId?: string;
    productId?: string;
    /** true = subscription auto-renewable; false = one-time */
    isSubscription?: boolean;
    /** JWS firmato da Apple — da inviare a apple-validate-receipt */
    signedTransaction?: string;
    /** Presente solo per subscription; assente per one-time */
    expirationDate?: string;
}

export interface DeskbayStoreEntitlement {
    transactionId: string;
    originalTransactionId: string;
    productId: string;
    isSubscription: boolean;
    expirationDate: string;
    isActive: boolean;
}

export interface DeskbayStorePlugin {
    /** Recupera tutti i 4 prodotti con prezzi localizzati da App Store */
    getProducts(): Promise<{ products: DeskbayStoreProduct[] }>;
    /** Avvia il flusso di acquisto StoreKit 2 (subscription + one-time) */
    purchase(options: { productId: string }): Promise<DeskbayStorePurchaseResult>;
    /** Ritorna le entitlements attive (subscription corrente + non-consumable) */
    getCurrentEntitlements(): Promise<{ entitlements: DeskbayStoreEntitlement[] }>;
    /** Ripristina acquisti dopo reinstall */
    restorePurchases(): Promise<{ entitlements: DeskbayStoreEntitlement[] }>;
    /** Apre le impostazioni abbonamento Apple (solo per subscription) */
    manageSubscription(): Promise<{ opened: boolean }>;
}

// ---------------------------------------------------------------------------
// Costanti Product ID
// ---------------------------------------------------------------------------

/** Product ID delle subscription auto-renewable */
export const APPLE_SUBSCRIPTION_IDS = [
    'io.deskbay.app.monthly',
    'io.deskbay.app.yearly',
] as const;

/** Product ID degli acquisti one-time */
export const APPLE_ONE_TIME_IDS = [
    'io.deskbay.app.setup.assisted',
    'io.deskbay.app.test.1eur',
] as const;

export type AppleSubscriptionId = typeof APPLE_SUBSCRIPTION_IDS[number];
export type AppleOneTimeId = typeof APPLE_ONE_TIME_IDS[number];
export type AppleProductId = AppleSubscriptionId | AppleOneTimeId;

// ---------------------------------------------------------------------------
// Registrazione plugin
// ---------------------------------------------------------------------------

const DeskbayStore = registerPlugin<DeskbayStorePlugin>('DeskbayStore');

export default DeskbayStore;
